import React, { useEffect, useState, useRef, useCallback } from "react";
import RoundImage from "./RoundImage";
import Buttonz from "./Buttonz";
import Popupz from "./Popupz";
import CustomSlider from "./Slider";
import SvgIcn from "../../data/IconCompo";
import "../../styles/atoms.scss";

function ImageUploader(props) {
  const [image, setImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState();
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialScale, setInitialScale] = useState(1);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const fileInputRef = useRef(null);
  /** Avatar shown before opening the crop popup; restored if user closes without Save. */
  const avatarBeforeEditRef = useRef(null);
  const croppedImageRef = useRef(null);

  useEffect(() => {
    croppedImageRef.current = croppedImage;
  }, [croppedImage]);

  const calculateInitialScale = (
    imgWidth,
    imgHeight,
    canvasWidth,
    canvasHeight
  ) => {
    const scaleX = canvasWidth / imgWidth;
    const scaleY = canvasHeight / imgHeight;
    return Math.max(scaleX, scaleY);
  };
  useEffect(() => {
    if (props.value && props.value !== croppedImage) {
      setCroppedImage(props.value);
      if (props.value.startsWith("data:image")) {
        const img = new Image();
        img.onload = () => {
          imageRef.current = img;
        };
        img.src = props.value;
      } else if (/^https?:\/\//.test(props.value)) {
        const img = new Image();
        img.onload = () => {
          imageRef.current = img;
        };
        img.onerror = () => {
          imageRef.current = null;
        };
        img.src = props.value;
      }
    }
  }, [props.value, croppedImage]);
  const constrainPosition = (newX, newY, scale) => {
    if (!imageRef.current || !canvasRef.current) return { x: newX, y: newY };

    const canvas = canvasRef.current;
    const img = imageRef.current;

    const scaledWidth = img.width * scale * initialScale;
    const scaledHeight = img.height * scale * initialScale;

    const maxX = (scaledWidth - canvas.width) / 2;
    const maxY = (scaledHeight - canvas.height) / 2;

    return {
      x: Math.min(Math.max(newX, -maxX), maxX),
      y: Math.min(Math.max(newY, -maxY), maxY),
    };
  };

  const loadImageRefFromSrc = (src) => {
    if (!src) {
      imageRef.current = null;
      return;
    }
    if (String(src).startsWith("data:image")) {
      const img = new Image();
      img.onload = () => {
        imageRef.current = img;
      };
      img.src = src;
    } else if (/^https?:\/\//.test(String(src))) {
      const img = new Image();
      img.onload = () => {
        imageRef.current = img;
      };
      img.onerror = () => {
        imageRef.current = null;
      };
      img.src = src;
    } else {
      imageRef.current = null;
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      avatarBeforeEditRef.current =
        croppedImageRef.current ?? props.value ?? null;
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvasW = canvasRef.current?.width || 200;
          const canvasH = canvasRef.current?.height || 200;
          const scale = calculateInitialScale(
            img.width,
            img.height,
            canvasW,
            canvasH
          );
          setInitialScale(scale);
          imageRef.current = img;
          setImage(event.target.result);
          setPosition({ x: 0, y: 0 });
          setZoom(1);
          // Directly set the cropped image and notify parent
          setCroppedImage(event.target.result);
          props.onChange(event.target.result);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      const constrained = constrainPosition(newX, newY, zoom);
      setPosition(constrained);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const drawImage = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = imageRef.current;
    if (!img || !img.complete) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create circular clip
    ctx.save();
    ctx.beginPath();
    ctx.arc(
      canvas.width / 2,
      canvas.height / 2,
      canvas.width / 2,
      0,
      Math.PI * 2,
      true
    );
    ctx.closePath();
    ctx.clip();

    // Calculate scaled dimensions
    const scale = zoom * initialScale;
    const scaledWidth = img.width * scale;
    const scaledHeight = img.height * scale;

    // Draw image with current position and zoom
    ctx.drawImage(
      img,
      canvas.width / 2 - scaledWidth / 2 + position.x,
      canvas.height / 2 - scaledHeight / 2 + position.y,
      scaledWidth,
      scaledHeight
    );

    ctx.restore();
  }, [image, zoom, position, initialScale]);

  const handleCrop = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const croppedDataUrl = canvas.toDataURL("image/png");
    setCroppedImage(croppedDataUrl);
    setImage(null);
    props.onChange(croppedDataUrl);
    avatarBeforeEditRef.current = croppedDataUrl;
    if (fileInputRef.current) fileInputRef.current.value = "";
    loadImageRefFromSrc(croppedDataUrl);
  };

  /** Close crop popup without saving: discard new file and show previous avatar. */
  const handlePopupDismiss = () => {
    setImage(null);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setInitialScale(1);
    if (fileInputRef.current) fileInputRef.current.value = "";

    const revert = avatarBeforeEditRef.current;
    avatarBeforeEditRef.current = null;

    if (revert != null && revert !== "") {
      setCroppedImage(revert);
      props.onChange(revert);
      loadImageRefFromSrc(revert);
    } else {
      setCroppedImage(null);
      props.onChange(undefined);
      imageRef.current = null;
    }
  };

  useEffect(() => {
    if (image && imageRef.current?.complete) {
      drawImage();
    }
  }, [zoom, position, image, drawImage]);

  return (
    <>
    <div className="avatar-box-wrapper">
      <div className={`avatar-box ${props.error ? "avatar-box-error" : ""}`}>
        <input
          ref={fileInputRef}
          type="file"
          id="profile-upload"
          onChange={handleImageChange}
          style={{ display: "none" }}
          accept="image/*"
        />
        <div
          className={`${props.className} avatar-upload-trigger`}
          onClick={() => {
            fileInputRef.current?.click();
          }}
          style={{ cursor: "pointer" }}
        >
          <RoundImage
            imageFull={true}
            imageData={croppedImage}
            alt="Cropped Avatar"
            error={props.error}
            noImage={<SvgIcn name="logo01" className="avatar-placeholder" />}
          />
        </div>
        {props.addIcon && !croppedImage && (
          <label htmlFor="profile-upload" className="avatar-icon">
            <SvgIcn name="image" />
          </label>
        )}
        {croppedImage && (
          <div
            className="avatar-icon trash-icon"
            onClick={(e) => {
              e.stopPropagation();
              setCroppedImage(null);
              props.onDelete();
            }}
          >
            <SvgIcn name="trash" />
          </div>
        )}
      </div>
      </div>

      {image && (
        <div className="container-editor image-upload-popup-editor">
          <Popupz
            title="Image Upload"
            onClose={handlePopupDismiss}
            className="image-upload-popup"
          >
            <div className="editor-container">
              <div
                style={{
                  touchAction: "none",
                  cursor: isDragging ? "grabbing" : "grab",
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <canvas
                  ref={canvasRef}
                  width={200}
                  height={200}
                  className="editor"
                />
              </div>

              <div className="slider-container">
                <CustomSlider
                  value={zoom}
                  min={1}
                  max={4}
                  step={0.1}
                  onChange={(e, value) => {
                    setZoom(value);
                    const constrained = constrainPosition(
                      position.x,
                      position.y,
                      value
                    );
                    setPosition(constrained);
                  }}
                  ariaLabel="zoom-slider"
                />
              </div>
              
              <div className="buttons-container">
                {/* <Buttonz
                  iconR="clear"
                  hoverR="clear"
                  label="Cancel"
                  onClick={handleClean}
                  className={"cancel"}
                /> */}
                <Buttonz
                  iconR="upload"
                  hoverR="upload"
                  label="SAVE"
                  onClick={handleCrop}

                />
              </div>
            </div>
          </Popupz>
        </div>
      )}
    </>
  );
}

export default ImageUploader;
